/// <amd-module name="Search/WorkItem/Flux/StoresHub" />
import * as CompositeStoresManager from "Search/Shared/Base/Stores/CompositeStoresManager";
import { Action } from "VSS/Reflux/Action";
import { ActionsHub } from "Search/WorkItem/Flux/ActionsHub";
import { EntitySortOption, EntitySearchResponseWithActivityId } from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { Filter } from "SearchUI/Utilities/Filter";
import { FilterStore } from "Search/WorkItem/Flux/Stores/FilterStore";
import { IFilterStoreState } from "Search/Shared/Base/Stores/FilterStore";
import { IPreviewOrientationStoreState } from "Search/Shared/Base/Stores/PreviewOrientationStore";
import { IPreviewSetting, ISortField, ISeedData } from "Search/Shared/Base/Types";
import { IProjectData } from "Tfs/Platform/Page";
import { ISearchStoreState } from "Search/Shared/Base/Stores/SearchStore";
import { ISeedDataSearchService, ISearchServiceFailedState } from "Search/Coordinator/SeedDataSearchService";
import { IStoresHub } from "Search/Shared/Base/Container";
import { ItemContentStore } from "Search/WorkItem/Flux/Stores/ItemContentStore";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";
import { NotificationStore, INotificationStoreState } from "Search/WorkItem/Flux/Stores/NotificationStore";
import { OrganizationInfoStore, IOrganizationInfoState } from "Search/Shared/Base/Stores/OrganizationInfoStore";
import { PreviewOrientationStore } from "Search/WorkItem/Flux/Stores/PreviewOrientationStore";
import { PreviewNotificationStore, IPreviewNotificationStoreState } from "Search/WorkItem/Flux/Stores/PreviewNotificationStore";
import { SearchSourceType } from "Search/Shared/Base/Sources/SearchSource";
import { SearchStore } from "Search/WorkItem/Flux/Stores/SearchStore";
import { SortOptionsStore } from "Search/WorkItem/Flux/Stores/SortOptionsStore";
import { WorkItemResult, WorkItemSearchRequest, WorkItemSearchResponse } from "Search/LegacyWebApi/Contracts/WorkItem";
import { WorkItemSearchPageDataProviderContributionId, WorkItemSearchOnPremPageDataProviderContributionId } from "Search/WorkItem/Constants";
import { ZeroDataState } from "Search/Shared/Base/Stores/ZeroDataStore";
import { ZeroDataStore } from "Search/WorkItem/Flux/Stores/ZeroDataStore";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import * as Constants from "Search/WorkItem/Constants";

export type StoreName =
    "searchStore" |
    "filterStore" |
    "itemContentStore" |
    "notificationStore" |
    "previewOrientationStore" |
    "sortOptionsStore" |
    "organizationInfoStore" |
    "previewNotificationStore" |
    "zeroDataStore";

export interface IAggregatedState {
    previewOrientationStoreState: IPreviewOrientationStoreState;
    searchStoreState: ISearchStoreState<WorkItemSearchRequest, WorkItemSearchResponse>;
    filterStoreState: IFilterStoreState;
    selectedItem: WorkItemResult;
    zeroDataState: ZeroDataState;
    isProjectContext: boolean;
    availableSortFields: ISortField[];
    appliedEntitySortOption: EntitySortOption;
    availablePreviewOrientations: IPreviewSetting[];
    notificationStoreState: INotificationStoreState;
    previewNotificationStoreState: IPreviewNotificationStoreState;
    organizationInfoState: IOrganizationInfoState;
}

export class StoresHub implements IStoresHub<StoreName, IAggregatedState> {
    private readonly compositeStoresManager = new CompositeStoresManager.CompositeStoresManager();
    private readonly listener: CompositeStoresManager.ListeningActionsManager;
    private readonly historyService: IVssHistoryService; 
    public searchStore: SearchStore;
    public sortOptionsStore: SortOptionsStore;
    public filterStore: FilterStore;
    public itemContentStore: ItemContentStore;
    public previewOrientationStore: PreviewOrientationStore;
    public zeroDataStore: ZeroDataStore;
    public notificationStore: NotificationStore;
    public organizationInfoStore: OrganizationInfoStore;
    public previewNotificationStore: PreviewNotificationStore;

    constructor(
        private readonly actionsHub: ActionsHub,
        private readonly filter: Filter,
        private readonly isAssignedToFilterEnabled: boolean,
        private readonly includeFacets: boolean,
        private readonly isHosted: boolean,
        private readonly pageContext: IVssPageContext,
        private readonly isTabSwitch: boolean,
        private readonly useSeedDataSearchService: boolean,
        private readonly project?: IProjectData,
        private readonly onDispatched?: CompositeStoresManager.EmitChangedFunction<any>
    ) {
        const projectName = this.project ? this.project.name : "";
        this.listener = new CompositeStoresManager.ListeningActionsManager(this.emitChanged);
        this.historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        this.searchStore = this.createSearchStore();
        this.sortOptionsStore = this.createSortOptionsStore(actionsHub);
        this.filterStore = this.createFilterStore(actionsHub, this.filter, !!this.project, projectName, this.isAssignedToFilterEnabled);
        this.itemContentStore = this.createItemContentStore();
        this.previewOrientationStore = this.createPreviewOrientationStore(this.actionsHub);
        this.zeroDataStore = this.createZeroDataStore(actionsHub);
        this.notificationStore = this.createNotificationStore(actionsHub);
        this.organizationInfoStore = this.createOrganizationInfoStore(actionsHub);
        this.previewNotificationStore = this.createPreviewNotificationStore(actionsHub);
    }

    public getAggregatedState = (): IAggregatedState => {
        // Sort the tags in the work item results based on the current browser locale.
        if(isFeatureFlagEnabled(this.pageContext, Constants.FeatureFlagConstants.EnableTagsSortingForWorkitem, false))
            {
                this.searchStore.state.response?.results.values.forEach((item: WorkItemResult) => {
                    if (item.fields) {
                        item.fields.forEach(field => {
                            if (field.name === "Tags" && field.referenceName === "system.tags" && field.value) {
                                var arr: string[] = field.value.split(";");
                                for (var i = 0; i < arr.length; i++) {
                                    arr[i] = arr[i].trim();
                                }                      
                                arr.sort((a, b) => a.localeCompare(b, navigator.language, { sensitivity: 'base' }));
                                field.value = arr.join(";");
                                arr = []; // Deallocate array
                            }
                        });
                    }
                });
            }
        return {
            isProjectContext: !!this.project,
            searchStoreState: this.searchStore.state,
            filterStoreState: this.filterStore.state,
            availableSortFields: this.sortOptionsStore.availableSortFields,
            appliedEntitySortOption: this.sortOptionsStore.state.sortOption,
            availablePreviewOrientations: this.previewOrientationStore.availablePreviewOrientations,
            selectedItem: this.itemContentStore.state.selectedItem,
            previewOrientationStoreState: this.previewOrientationStore.state,
            zeroDataState: this.zeroDataStore.state,
            notificationStoreState: this.notificationStore.state,
            organizationInfoState: this.organizationInfoStore.state,
            previewNotificationStoreState: this.previewNotificationStore.state
        };
    };

    public getCompositeStore(storeNames: StoreName[]): CompositeStoresManager.CompositeStore {
        return this.compositeStoresManager.getOrCreate(storeNames);
    }

    public dispose = (): void => {
        this.listener.dispose();
        this.compositeStoresManager.dispose();
    };

    private get seedData(): ISeedData<WorkItemResult> | undefined {
        const routeParams: { [key: string]: string } = this.historyService.getState();

        let responseWithActivityId: EntitySearchResponseWithActivityId | undefined;
        let error: ISearchServiceFailedState | undefined = undefined;
        let source: SearchSourceType;
        
        if(this.isHosted && this.useSeedDataSearchService && routeParams.udp === "false") {
            const seedDataSearchService: ISeedDataSearchService = this.pageContext.getService<ISeedDataSearchService>("ISeedDataSearchService");
            let seedData = seedDataSearchService.getSearchServiceState();
            source = SearchSourceType.RestApi;
            
            if (seedData.searchText === routeParams.text) {
                if(seedData.responseWithActivityId) {
                    responseWithActivityId = seedData.responseWithActivityId;
                }
                else if(seedData.error) {
                    error = seedData.error;
                }
            }
        } else {
            const dataProviderContributionId = this.isHosted
                ? WorkItemSearchPageDataProviderContributionId
                : WorkItemSearchOnPremPageDataProviderContributionId;

            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            responseWithActivityId = contributionService.getData<EntitySearchResponseWithActivityId | undefined>(dataProviderContributionId);
            source = SearchSourceType.DataProvider;
        }
        
        if (!this.isTabSwitch && responseWithActivityId && responseWithActivityId.response) {
            const response = responseWithActivityId.response as WorkItemSearchResponse;
            const items = response && response.results && response.results.values;
            const activeItem = items && items.length > 0 ? items[0] : undefined;
            return {
                responseWithActivityId,
                source,
                activeItem
            };
        }
        else if (!this.isTabSwitch && error) {
            return { error, source };
        }
    }

    private createPreviewOrientationStore(actionsHub: ActionsHub): PreviewOrientationStore {
        const store = new PreviewOrientationStore("PreviewOrientationStoreChanged", this.pageContext);

        this.listener.listen(actionsHub.previewOrientationChanged, "previewOrientationStore", store.updatePreviewOrientationMode);

        return store;
    }

    private createSearchStore(): SearchStore {
        const store = new SearchStore("SearchStoreChanged", this.seedData);

        this.listener.listen(this.actionsHub.pageInitializationStarted, "searchStore", (payload) => {
            payload.isLandingPage ? store.updateQuery(payload) : store.startSearch(payload);
        });
        this.listener.listen(this.actionsHub.resultsLoaded, "searchStore", store.loadSearchResults);
        this.listener.listen(this.actionsHub.searchStarted, "searchStore", store.startSearch);
        this.listener.listen(this.actionsHub.sortOptionChanged, "searchStore", store.updateItemsOnSort);
        this.listener.listen(this.actionsHub.searchFailed, "searchStore", store.failSearch);

        return store;
    }

    private createFilterStore(
        actionsHub: ActionsHub,
        filter: Filter,
        isProjectContext: boolean,
        projectName: string,
        isAssignedToFilterEnabled: boolean
    ): FilterStore {
        const store = new FilterStore(
            "FilterStoreChanged",
            isProjectContext,
            isAssignedToFilterEnabled,
            filter,
            projectName,
            this.pageContext,
            this.seedData);

        this.listener.listen(actionsHub.facetsLoaded, "filterStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.updateFilters(searchStoreState.query, payload.response);
        });

        // Listen on resultsLoaded only when includeFacets is true otherwise during filter application
        // scenario, filter component would be notified on resultsLoaded also resulting in undesired state.
        if (this.includeFacets) {
            this.listener.listen(actionsHub.resultsLoaded, "filterStore", (payload) => {
                const { searchStoreState } = this.getAggregatedState();
                store.updateFilters(searchStoreState.query, payload.response);
            });
        }

        this.listener.listen(actionsHub.fetchfacetsFailed, "filterStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.failFacetsLoad(searchStoreState.query, payload);
        });

        this.listener.listen(actionsHub.filterPaneToggled, "filterStore", store.toggleFilterPane);

        return store;
    }

    public createSortOptionsStore(actionsHub: ActionsHub): SortOptionsStore {
        const store = new SortOptionsStore("SortOptionsStoreChanged", this.seedData);

        this.listener.listen(actionsHub.sortOptionChanged, "sortOptionsStore", store.changeSortOption);
        this.listener.listen(actionsHub.pageInitializationStarted, "sortOptionsStore", (payload) => {
            !payload.isLandingPage && store.updateSortOptionOnSearch(payload);
        });
        this.listener.listen(actionsHub.searchStarted, "sortOptionsStore", store.updateSortOptionOnSearch);

        return store;
    }

    private createItemContentStore(): ItemContentStore {
        const store = new ItemContentStore("ItemContentStoreChanged", this.seedData);
        this.listener.listen(this.actionsHub.itemChanged, "itemContentStore", store.changeActiveItem);
        this.listener.listen(this.actionsHub.resultsLoaded, "itemContentStore", payload => {
            store.changeActiveItem({ item: payload.activeItem });
        });
        this.listener.listen(this.actionsHub.sortOptionChanged, "itemContentStore", payload => {
            store.changeActiveItem({ item: payload.sortedItems[0] });
        });

        return store;
    }

    public createNotificationStore(actionsHub: ActionsHub): NotificationStore {
        const store = new NotificationStore("NotificationStoreChanged", this.seedData);

        this.listener.listen(actionsHub.resultsLoaded, "notificationStore", store.onSearchResultsLoaded);

        return store;
    }

    public createPreviewNotificationStore(actionsHub: ActionsHub): PreviewNotificationStore {
        const store = new PreviewNotificationStore("PreviewNotificationStoreChanged");

        this.listener.listen(actionsHub.showPreviewMessageBanner, "previewNotificationStore", store.showWorkItemPreviewBanner);
        this.listener.listen(actionsHub.dismissPreviewMessageBanner, "previewNotificationStore", store.dismissWorkItemPreviewBanner);
        this.listener.listen(actionsHub.resultsLoaded, "previewNotificationStore", store.dismissWorkItemPreviewBanner);

        return store;
    }

    private createOrganizationInfoStore(actionsHub: ActionsHub): OrganizationInfoStore {
        const store = new OrganizationInfoStore("OrganizationInfoStoreChanged");
        this.listener.listen(actionsHub.organizationInfoLoaded, "organizationInfoStore", store.onOrganizationInfoLoaded);
        this.listener.listen(actionsHub.organizationInfoLoadFailed, "organizationInfoStore", store.onOrganizationInfoLoadFailed);
        this.listener.listen(actionsHub.errorNotificationBannerDismissed, "organizationInfoStore", store.onResetOrganizationInfoLoadStatus);
        this.listener.listen(actionsHub.searchStarted, "organizationInfoStore", store.onResetOrganizationInfoLoadStatus);

        return store;
    }

    private createZeroDataStore(actionsHub: ActionsHub): ZeroDataStore {
        const store = new ZeroDataStore("ZeroDataStoreChanged", this.seedData);

        this.listener.listen(actionsHub.pageInitializationStarted, "zeroDataStore", (payload) => {
            if (payload.isLandingPage) {
                store.onLandingPage();
            }
        });
        this.listener.listen(actionsHub.resultsLoaded, "zeroDataStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            const { query } = searchStoreState;
            store.onResultsLoaded(query, payload);
        });
        this.listener.listen(actionsHub.searchFailed, "zeroDataStore", (payload) => {
            const { searchStoreState } = this.getAggregatedState();
            store.onSearchFailed(payload, searchStoreState.query);
        });
        this.listener.listen(actionsHub.searchStarted, "zeroDataStore", store.reset);

        return store;
    }

    private emitChanged = (changedStores: string[], action: Action<any>, payload: any): void => {
        this.compositeStoresManager.emitCompositeChanged(changedStores);

        if (this.onDispatched) {
            this.onDispatched(changedStores, action, payload);
        }
    };
}
